import {
  Stack,
  Text,
  Button,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  Heading,
  VStack,
} from '@chakra-ui/react';
import type { NestedHome, NestedViewing } from '@habity/types';
import {
  formatNextViewing,
  isViewingFullyBooked,
  getStreet,
} from '@habity/services';
import { useState, useEffect } from 'react';
import { useViewing } from '~/hooks/useViewing';
import { useUser } from '~/hooks/useUser';
import { useAppStore } from '~/store';
import { useUIStore } from '~/store/useUIStore';
import { ViewingSlotSelector } from './ViewingSlotSelector';
import { ProfileHeaderMobile } from '../../mobile/Header';
import { MdArrowBack } from 'react-icons/md';

interface ViewingsProps {
  viewings: NestedViewing[];
  home: NestedHome;
  isModal?: boolean;
  onViewingSelect?: (viewing: NestedViewing) => void; // Callback when viewing with multiple slots is selected
  onOpenViewingModal?: (viewingId?: number) => void; // Callback to open external viewing modal
}

export const Viewings = ({
  viewings,
  home,
  isModal = false,
  onViewingSelect,
  onOpenViewingModal,
}: ViewingsProps) => {
  const [selectedViewing, setSelectedViewing] = useState<NestedViewing | null>(
    null
  );
  const [selectedSlotId, setSelectedSlotId] = useState<string>('');
  const [showSlotSelector, setShowSlotSelector] = useState(false);

  // Track loading state for each viewing separately
  const [loadingViewings, setLoadingViewings] = useState<
    Record<number, boolean>
  >({});
  const { addToViewing, removeFromViewing } = useViewing();
  const { user } = useUser();
  const isMobile = useAppStore((state) => state.isMobile);

  // Use the viewingRegistrationCache from the hook directly
  const [registeredViewings, setRegisteredViewings] = useState<
    Record<number, boolean>
  >({});

  // Initialize registered viewings state based on viewing slot participants
  // Check if user is registered in any slot for each viewing
  useEffect(() => {
    if (!user || !viewings || viewings.length === 0) return;

    const newRegisteredViewings: Record<number, boolean> = {};

    for (const viewing of viewings) {
      // Check if the user is registered in any slot's participants
      if (viewing.viewing_slots && Array.isArray(viewing.viewing_slots)) {
        newRegisteredViewings[viewing.id] = viewing.viewing_slots.some(
          (slot) => {
            if (
              !slot.viewing_participants ||
              !Array.isArray(slot.viewing_participants)
            ) {
              return false;
            }
            return slot.viewing_participants.some(
              (participant) => participant && participant.user_id === user.id
            );
          }
        );
      } else {
        newRegisteredViewings[viewing.id] = false;
      }
    }

    setRegisteredViewings(newRegisteredViewings);
  }, [user, viewings]);

  // Handle viewing registration (regardless of booking URL)
  const handleViewingClick = async (viewing: NestedViewing) => {
    // Don't do anything if action is already in progress
    if (loadingViewings[viewing.id]) return;

    // Check if viewing has multiple slots - show selector even if not logged in
    const hasMultipleSlots =
      viewing.viewing_slots && viewing.viewing_slots.length > 1;

    // Get current registration state
    const isRegistered = registeredViewings[viewing.id];

    if (isRegistered) {
      // If already registered, unregister directly
      await handleUnregisterFromViewing(viewing);
      return;
    }

    // For unregistered users, check if we need slot selection
    if (hasMultipleSlots) {
      // If we're in a modal with onViewingSelect callback, use that (priority)
      if (isModal && onViewingSelect) {
        onViewingSelect(viewing);
        return;
      }
      // If we have an external modal callback, use that
      if (onOpenViewingModal) {
        onOpenViewingModal(viewing.id);
        return;
      }
      // Otherwise show the inline slot selector modal
      setSelectedViewing(viewing);
      setShowSlotSelector(true);
      return;
    }

    // Single slot handling
    const slotId = viewing.viewing_slots?.[0]?.id;
    if (!slotId) {
      console.error('No slot ID found for viewing:', viewing);
      return;
    }

    // If user is not logged in
    if (!user) {
      // If we're in a modal with onViewingSelect callback, use that (priority)
      if (isModal && onViewingSelect) {
        onViewingSelect(viewing);
        return;
      }
      // If we have an external modal callback, use that
      if (onOpenViewingModal) {
        onOpenViewingModal(viewing.id);
        return;
      }
      // Otherwise show the inline slot selector modal
      setSelectedViewing(viewing);
      setShowSlotSelector(true);
      return;
    }

    // User is logged in, register for single slot
    await handleRegisterForViewing(viewing, slotId);
  };

  const handleRegisterForViewing = async (
    viewing: NestedViewing,
    slotId: number
  ) => {
    // Set loading state for this specific viewing
    setLoadingViewings((prev) => ({ ...prev, [viewing.id]: true }));

    // Immediately update UI state (optimistic update)
    setRegisteredViewings((prev) => ({ ...prev, [viewing.id]: true }));

    try {
      // Add user to viewing with optional slot
      const result = await addToViewing(home, viewing, slotId);

      // If the API call failed, revert the optimistic update
      if (!result.success) {
        setRegisteredViewings((prev) => ({ ...prev, [viewing.id]: false }));
      }
    } catch (error) {
      console.error('Error registering for viewing:', error);
      // Revert to original state if there's an error
      setRegisteredViewings((prev) => ({ ...prev, [viewing.id]: false }));
    } finally {
      // Reset loading state
      setLoadingViewings((prev) => ({ ...prev, [viewing.id]: false }));
    }
  };

  const handleUnregisterFromViewing = async (viewing: NestedViewing) => {
    // Set loading state for this specific viewing
    setLoadingViewings((prev) => ({ ...prev, [viewing.id]: true }));

    // Immediately update UI state (optimistic update)
    setRegisteredViewings((prev) => ({ ...prev, [viewing.id]: false }));

    try {
      // Remove user from viewing
      const result = await removeFromViewing(home, viewing);

      // If the API call failed, revert the optimistic update
      if (!result.success) {
        setRegisteredViewings((prev) => ({ ...prev, [viewing.id]: true }));
      }
    } catch (error) {
      console.error('Error unregistering from viewing:', error);
      // Revert to original state if there's an error
      setRegisteredViewings((prev) => ({ ...prev, [viewing.id]: true }));
    } finally {
      // Reset loading state
      setLoadingViewings((prev) => ({ ...prev, [viewing.id]: false }));
    }
  };

  const handleSlotSelect = async (slotId: number) => {
    if (!selectedViewing) return;

    // Store the viewing slot ID in the store for the booking request
    const { setLoginViaViewingSlotId } = useUIStore.getState();
    setLoginViaViewingSlotId(slotId);

    await handleRegisterForViewing(selectedViewing, slotId);
    setSelectedViewing(null);
    setShowSlotSelector(false);
  };

  const handleSlotSelectorBack = () => {
    setShowSlotSelector(false);
    setSelectedViewing(null);
    setSelectedSlotId('');
  };

  if (!viewings || !viewings.length) return null;

  return (
    <Stack gap="3">
      {/* Label */}
      <Text
        size={{ mobile: 'sm', desktop: 'md' }}
        fontWeight="light"
        color="blackAlpha.800"
      >
        Kommande visningar
      </Text>

      {/* Always show vertical list (modal, mobile, and desktop) */}
      <Stack pt="2" spacing="8">
        {viewings.map((viewing: NestedViewing) => (
          <ViewingCard
            home={home}
            key={viewing.id}
            viewing={viewing}
            onClick={() => handleViewingClick(viewing)}
            isLoading={loadingViewings[viewing.id]}
            isRegistered={registeredViewings[viewing.id]}
            isModal={isModal}
          />
        ))}
      </Stack>

      {/* Slot Selector Modal */}
      <Modal
        allowPinchZoom
        size="full"
        isOpen={showSlotSelector}
        onClose={handleSlotSelectorBack}
        returnFocusOnClose={false}
        motionPreset={isMobile ? 'none' : 'scale'}
      >
        <ModalOverlay />
        <ModalContent backgroundColor="white">
          <Box
            w="full"
            h="80px"
            position="sticky"
            backgroundColor="white"
            boxShadow="lg"
            zIndex="1"
          >
            <ProfileHeaderMobile
              icon={<MdArrowBack size="30" />}
              title={getStreet(home)}
              onClickBack={handleSlotSelectorBack}
            />
          </Box>

          <Box p={{ base: 4, md: 8 }} maxW="800px" mx="auto" w="full">
            <VStack spacing={6} align="stretch">
              <Heading as="h2" size="lg">
                Välj tid för visning
              </Heading>

              {selectedViewing && (
                <ViewingSlotSelector
                  viewing={selectedViewing}
                  home={home}
                  selectedSlotId={selectedSlotId}
                  setSelectedSlotId={setSelectedSlotId}
                  onSlotSelect={handleSlotSelect}
                  onBack={handleSlotSelectorBack}
                  isLoading={loadingViewings[selectedViewing.id]}
                  requiresAuth={!user}
                />
              )}
            </VStack>
          </Box>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

interface ViewingCardProps {
  viewing: NestedViewing;
  onClick: () => void;
  isLoading?: boolean;
  isRegistered?: boolean;
  isModal?: boolean;
  home: NestedHome;
}

function ViewingCard({
  viewing,
  onClick,
  isLoading = false,
  isRegistered = false,
  isModal = false,
  home,
}: ViewingCardProps) {
  const { responsible_broker } = home || {};
  const isMobile = useAppStore((state) => state.isMobile);

  // Check if viewing is fully booked
  const isFullyBooked = isViewingFullyBooked(viewing);

  const handleContactClick = () => {
    if (!responsible_broker) return;

    const homeAddress = home?.address?.street || 'denna bostad';
    const viewingDate = formatNextViewing(viewing, true);
    const subject = `Anmälan till visning av ${homeAddress}`;
    const body = isMobile
      ? `Anmälan till visning av ${homeAddress}%0D%0A${viewingDate}%0D%0A%0D%0A`
      : `Hej,%0D%0A%0D%0AJag vill anmäla mig till visning av ${homeAddress}.%0D%0A%0D%0AVisning: ${viewingDate}%0D%0A%0D%0AMed vänliga hälsningar`;

    const contactUrl =
      isMobile && responsible_broker.phone
        ? `sms:${responsible_broker.phone}&body=${subject}%0D%0A${viewingDate}%0D%0A%0D%0A`
        : `mailto:${
            responsible_broker.email || ''
          }?subject=${subject}&body=${body}`;

    window.location.href = contactUrl;
  };

  return (
    <Stack spacing="1">
      <Text fontWeight="medium" size={{ mobile: 'md', desktop: 'xl' }}>
        {formatNextViewing(viewing, true)}
      </Text>
      <Text size="sm" color="blackAlpha.700" noOfLines={3}>
        {viewing.description}
      </Text>

      <Box mt="3">
        {viewing.is_bookable ? (
          <Button
            variant={isModal ? 'primary' : 'secondary'}
            borderRadius="4px"
            onClick={onClick}
            size={isModal ? 'md' : 'sm'}
            fontSize={isModal ? 'md' : 'sm'}
            isDisabled={isFullyBooked && !isRegistered}
          >
            {isLoading
              ? 'Laddar...'
              : isRegistered
              ? 'Avboka visning'
              : isFullyBooked
              ? 'Fullbokad'
              : 'Boka visning'}
          </Button>
        ) : (
          <Button
            variant={isModal ? 'primary' : 'secondary'}
            borderRadius="4px"
            onClick={handleContactClick}
            size={isModal ? 'md' : 'sm'}
            fontSize={isModal ? 'md' : 'sm'}
          >
            Kontakta mäklare
          </Button>
        )}
      </Box>
    </Stack>
  );
}
